import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTerminal, faDatabase, faPlus, faCopy, faTimes, faDownload, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import axios from "axios";
import { getApiUrl } from "../utils/envLoader";
import { devService } from "../services/dev";

const Dev = () => {
  const [showModal, setShowModal] = useState(false);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [jsonData, setJsonData] = useState("{}");
  const [loading, setLoading] = useState(false);
  const [jsonError, setJsonError] = useState("");
  const [environment, setEnvironment] = useState({});
  const [generatingReport, setGeneratingReport] = useState(false);

  // Carrega as tabelas disponíveis ao abrir o modal
  useEffect(() => {
    const loadTables = async () => {
      try {
        const apiUrl = getApiUrl();
        const response = await axios.get(`${apiUrl}/api/dev/tables`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
          },
        });
        setTables(response.data.tables || []);
        if (response.data.tables && response.data.tables.length > 0) {
          setSelectedTable(response.data.tables[0]);
        }
      } catch (error) {
        console.error("Erro ao carregar tabelas:", error);
        toast.error("Erro ao carregar lista de tabelas");
      }
    };

    if (showModal) {
      loadTables();
    }
  }, [showModal]);

  // Carrega informações do ambiente
  useEffect(() => {
    setEnvironment({
      NODE_ENV: process.env.NODE_ENV || "unknown",
      REACT_APP_API_URL: process.env.REACT_APP_API_URL || "não definido",
      timestamp: new Date().toISOString(),
    });
  }, []);

  // Valida JSON enquanto o usuário digita
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonData(value);

    try {
      JSON.parse(value);
      setJsonError("");
    } catch (error) {
      setJsonError(`JSON inválido: ${error.message}`);
    }
  };

  // Formata o JSON automaticamente
  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonData);
      setJsonData(JSON.stringify(parsed, null, 2));
      setJsonError("");
      toast.success("JSON formatado com sucesso");
    } catch (error) {
      setJsonError(`JSON inválido: ${error.message}`);
      toast.error("JSON inválido não pode ser formatado");
    }
  };

  // Insere os dados na tabela selecionada
  const handleInsertData = async () => {
    if (!selectedTable) {
      toast.error("Selecione uma tabela");
      return;
    }

    if (jsonError) {
      toast.error("JSON contém erros");
      return;
    }

    try {
      const parsedData = JSON.parse(jsonData);
      setLoading(true);

      const apiUrl = getApiUrl();
      const response = await axios.post(
        `${apiUrl}/api/dev/insert`,
        {
          table: selectedTable,
          data: parsedData,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
          },
        }
      );

      toast.success(`✓ Dados inseridos com sucesso na tabela "${selectedTable}"`);
      console.log("Resposta do servidor:", response.data);

      // Limpa o formulário
      setJsonData("{}");
      setJsonError("");
      setShowModal(false);
    } catch (error) {
      console.error("Erro ao inserir dados:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Erro desconhecido";
      toast.error(`Erro ao inserir dados: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Copia exemplo de JSON para a área de transferência
  const copyExampleJson = (example) => {
    setJsonData(JSON.stringify(example, null, 2));
    setJsonError("");
  };

  // Gera relatório de NIFs duplicados
  const handleGenerateDuplicateNifReport = async () => {
    try {
      setGeneratingReport(true);
      await devService.downloadDuplicateNifReport();
      toast.success("✓ Relatório baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      toast.error(error.message || "Erro ao gerar relatório de NIFs duplicados");
    } finally {
      setGeneratingReport(false);
    }
  };

  const examples = {
    products: {
      product_code: "QJ002",
      name: "Queijo Mussarela",
      description: "Queijo de Mussarela",
      category: "Mussarela",
      is_cured: false,
      initial_weight_kg: 1.0,
      current_stock_kg: 50,
    },
    clientes: {
      nome: "João Silva",
      empresa: "Silva Distribuidora",
      nif: "123456789",
      zona: "Centro",
      email: "joao@example.com",
      contacto: "912345678",
      tipo_cliente: "Distribuidor",
    },
    orders: {
      order_number: "ORD0001",
      client_name: "Cliente Teste",
      client_nif: "999999999",
      delivery_date: "2025-10-30",
      total_weight_kg: 10.5,
      delivery_status: "pending",
    },
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <FontAwesomeIcon
            icon={faTerminal}
            className="w-8 h-8 text-blue-600"
          />
          <h1 className="text-3xl font-bold text-gray-900">Developer Console</h1>
        </div>
        <p className="text-gray-600 text-sm">
          Ferramentas de desenvolvimento para testes e debugging
        </p>
      </div>

      {/* Ambiente Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-3">Informações do Ambiente</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-blue-700 font-medium">NODE_ENV:</span>
              <span className="ml-2 text-blue-600">
                {environment.NODE_ENV}
              </span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">API URL:</span>
              <span className="ml-2 text-blue-600">
                {environment.REACT_APP_API_URL}
              </span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Horário:</span>
              <span className="ml-2 text-blue-600">
                {environment.timestamp ? new Date(environment.timestamp).toLocaleString("pt-PT") : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-900 mb-3">Status da Conexão</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-700">Frontend Conectado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-700">Backend Acessível</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-700">Database Disponível</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ferramentas */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faDatabase} className="text-blue-600" />
          Ferramentas de Dados
        </h2>

        <div className="space-y-4">
          {/* Botão Inserir JSON */}
          <button
            onClick={() => setShowModal(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} />
            Inserir JSON na Tabela
          </button>

          {/* Botão Relatório de NIFs Duplicados */}
          <button
            onClick={handleGenerateDuplicateNifReport}
            disabled={generatingReport}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faDownload} />
            {generatingReport ? "Gerando..." : "Gerar Relatório de NIFs Duplicados"}
          </button>

          {/* Info sobre as tabelas disponíveis */}
          <div className="bg-gray-50 p-4 rounded-lg text-sm">
            <p className="text-gray-700">
              <strong>Tabelas Disponíveis:</strong>
            </p>
            <div className="mt-2 space-y-1 text-gray-600">
              <p>• <code className="bg-gray-200 px-2 py-1 rounded">products</code> - Catálogo de queijos</p>
              <p>• <code className="bg-gray-200 px-2 py-1 rounded">clientes</code> - Dados de clientes</p>
              <p>• <code className="bg-gray-200 px-2 py-1 rounded">orders</code> - Encomendas</p>
              <p>• <code className="bg-gray-200 px-2 py-1 rounded">production_lots</code> - Lotes de produção</p>
              <p>• E outras tabelas do schema...</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para Inserir JSON */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className="dev-modal"
      >
        <Modal.Header closeButton className="bg-gray-50 border-bottom">
          <Modal.Title className="font-bold flex items-center gap-2">
            <FontAwesomeIcon icon={faDatabase} className="text-blue-600" />
            Inserir Dados JSON
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-6">
          {/* Select de Tabelas */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Selecione a Tabela
            </label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Selecione uma tabela --</option>
              {tables.map((table) => (
                <option key={table} value={table}>
                  {table}
                </option>
              ))}
            </select>
          </div>

          {/* Campo JSON */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                JSON Data
              </label>
              <button
                onClick={handleFormatJson}
                className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded transition-colors"
              >
                Formatar
              </button>
            </div>

            <textarea
              value={jsonData}
              onChange={handleJsonChange}
              className={`w-full h-64 px-4 py-3 border-2 rounded-lg font-mono text-sm resize-none focus:outline-none transition-colors ${
                jsonError
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300 focus:border-blue-500"
              }`}
              placeholder="Insira o JSON aqui..."
            />

            {jsonError && (
              <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                <span>⚠</span> {jsonError}
              </p>
            )}
          </div>

          {/* Exemplos de JSON */}
          {selectedTable && examples[selectedTable] && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Exemplo para "{selectedTable}":
              </p>
              <pre className="text-xs bg-white border border-blue-200 rounded p-2 mb-2 overflow-x-auto">
                {JSON.stringify(examples[selectedTable], null, 2)}
              </pre>
              <button
                onClick={() => copyExampleJson(examples[selectedTable])}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faCopy} />
                Copiar Exemplo
              </button>
            </div>
          )}

          {/* Aviso */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              ⚠️ <strong>Aviso:</strong> Esta ferramenta é apenas para desenvolvimento. Use com cuidado!
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-gray-50 border-top">
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertData}
            disabled={loading || !selectedTable || jsonError}
            className="bg-blue-600 hover:bg-blue-700 border-0"
          >
            {loading ? "Inserindo..." : "Inserir"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Dev;
